import { PageLocaleEnum } from 'common/enums/PageLocaleEnum'
import { BorderType } from 'common/types/styleTypes'
import { AnyAction } from 'redux'
import { getWebsiteLayoutPageIdForLocale } from 'src/editor/shared/utils/get-website-layout-page-id'
import {
  WebsiteButtonsInterface,
  WebsiteFontSettingsInterface,
  WebsiteFormFieldsSettings,
  WebsiteLayoutSettings,
  WebsitePageDataInterface,
  WebsiteSettingsInterface,
  WebsiteSettingsState,
  WebsiteStylesInterface,
  WebsiteThemeInterface,
  WebsiteThemeListInterface,
  WebsiteTypographySettingsStylesInterface,
} from 'src/shared/types/website-settings-types'
import {
  ADD_WEBSITE_LAYOUT,
  REMOVE_WEBSITE_LAYOUT,
  SET_CURRENT_WEBSITE_LOCALE,
  SET_WEBSITE_DATA_BASELINE,
  SET_WEBSITE_DRAFT_THEME,
  SET_WEBSITE_LAYOUT_BY_LOCALE,
  SET_WEBSITE_LOCALE,
  SET_WEBSITE_NAME,
  SET_WEBSITE_SETTINGS,
  SET_WEBSITE_STYLES,
  SET_WEBSITE_THEME,
  SET_WEBSITE_THEMES_LIST,
  UPDATE_WEBSITE_STYLES_FORM_FIELD,
  UPDATE_WEBSITE_STYLES_FORM_FIELD_BORDER,
  UPDATE_WEBSITE_STYLES_FORM_FIELD_TYPOGRAPHY,
  UPDATE_WEBSITE_STYLES_LAYOUT_SETTINGS,
  UPDATE_WEBSITE_STYLES_PRIMARY_BUTTONS,
  UPDATE_WEBSITE_STYLES_PRIMARY_BUTTONS_BORDER,
  UPDATE_WEBSITE_STYLES_PRIMARY_BUTTONS_TYPOGRAPHY,
  UPDATE_WEBSITE_STYLES_SECONDARY_BUTTONS,
  UPDATE_WEBSITE_STYLES_SECONDARY_BUTTONS_BORDER,
  UPDATE_WEBSITE_STYLES_SECONDARY_BUTTONS_TYPOGRAPHY,
  UPDATE_WEBSITE_STYLES_TYPOGRAPHY_HEADING,
  UPDATE_WEBSITE_STYLES_TYPOGRAPHY_PARAGRAPH,
  UPDATE_WEBSITE_THEME_LINKS,
} from './website-settings-action-type'

const defaultReducerState: WebsiteSettingsState = {
  websiteStyles: undefined,
  websiteThemesList: undefined,
  websiteTheme: undefined,
  websiteDraftTheme: null,
  websiteId: undefined,
  websiteLayouts: [],
  websiteLayoutPageId: undefined,
  websiteDataBaseline: undefined,
}

// NOTE: mock
// const defaultReducerState: WebsiteSettingsState = {
//   websiteStyles: {
//     typography: {
//       paragraph: {
//         fontFamily: 'Arial',
//         fontSize: 16,
//         fontWeight: '400',
//         lineHeight: 1.5,
//         textColor: '#000000',
//         backgroundColor: '#ffffff',
//       },
//       heading: {
//         fontFamily: 'Helvetica',
//         fontSize: 24,
//         fontWeight: '700',
//         lineHeight: 1.2,
//         textColor: '#000000',
//         backgroundColor: '#ffffff',
//       },
//     },
//     buttonsAndLinks: {
//       primary: {
//         typography: {
//           fontFamily: 'Arial',
//           fontSize: 16,
//           fontWeight: '400',
//           lineHeight: 1.5,
//         },
//         border: {
//           color: '#000000',
//           style: BorderStyleEnum.solid,
//           width: 2,
//           type: BorderTypeEnum.fullBorder,
//           radius: {
//             borderTopLeftRadius: 5,
//             borderTopRightRadius: 5,
//             borderBottomRightRadius: 5,
//             borderBottomLeftRadius: 5,
//           },
//         },
//         padding: {
//           paddingTop: 10,
//           paddingRight: 15,
//           paddingBottom: 10,
//           paddingLeft: 15,
//         },
//       },
//     },
//     formFields: {
//       typography: {
//         fontFamily: 'Arial',
//         fontSize: 16,
//         fontWeight: '400',
//         lineHeight: 1.5,
//       },
//       border: {
//         color: '#000000',
//         style: BorderStyleEnum.solid,
//         width: 2,
//         type: BorderTypeEnum.fullBorder,
//         radius: {
//           borderTopLeftRadius: 5,
//           borderTopRightRadius: 5,
//           borderBottomRightRadius: 5,
//           borderBottomLeftRadius: 5,
//         },
//       },
//       padding: {
//         paddingTop: 10,
//         paddingRight: 15,
//         paddingBottom: 10,
//         paddingLeft: 15,
//       },
//       style: 'filled',
//     },
//   },
//   websiteDraftTheme: websiteTheme1(),
//   websiteTheme: websiteTheme1(),
//   websiteId: 1,
//   websiteLayoutPageId: 1,
// }

export interface WebsiteStylesAction {
  type: typeof SET_WEBSITE_STYLES
  payload: WebsiteStylesInterface
}

export interface WebsiteNameAction {
  type: typeof SET_WEBSITE_NAME
  payload: string
}

export interface WebsiteCurrentLocaleAction {
  type: typeof SET_CURRENT_WEBSITE_LOCALE
  payload: PageLocaleEnum
}

export interface WebsiteLocaleAction {
  type: typeof SET_WEBSITE_LOCALE
  payload: PageLocaleEnum
}

export interface WebsiteStylesTypographyParagraphAction<
  T extends
    WebsiteTypographySettingsStylesInterface = WebsiteTypographySettingsStylesInterface,
> {
  type: typeof UPDATE_WEBSITE_STYLES_TYPOGRAPHY_PARAGRAPH
  payload: T
}

export interface WebsiteStylesTypographyHeadingAction<
  T extends
    WebsiteTypographySettingsStylesInterface = WebsiteTypographySettingsStylesInterface,
> {
  type: typeof UPDATE_WEBSITE_STYLES_TYPOGRAPHY_HEADING
  payload: T
}

export interface WebsiteStylesPrimaryButtonsTypographyAction<
  T extends WebsiteFontSettingsInterface = WebsiteFontSettingsInterface,
> {
  type: typeof UPDATE_WEBSITE_STYLES_PRIMARY_BUTTONS_TYPOGRAPHY
  payload: T
}

export interface WebsiteStylesSecondaryButtonsTypographyAction<
  T extends WebsiteFontSettingsInterface = WebsiteFontSettingsInterface,
> {
  type: typeof UPDATE_WEBSITE_STYLES_SECONDARY_BUTTONS_TYPOGRAPHY
  payload: T
}

export interface WebsiteStylesFormFieldTypographyAction<
  T extends WebsiteFontSettingsInterface = WebsiteFontSettingsInterface,
> {
  type: typeof UPDATE_WEBSITE_STYLES_FORM_FIELD_TYPOGRAPHY
  payload: T
}
export interface WebsiteStylesPrimaryButtonsBorderAction<
  T extends Partial<BorderType> = Partial<BorderType>,
> {
  type: typeof UPDATE_WEBSITE_STYLES_PRIMARY_BUTTONS_BORDER
  payload: T
}

export interface WebsiteStylesSecondaryButtonsBorderAction<
  T extends Partial<BorderType> = Partial<BorderType>,
> {
  type: typeof UPDATE_WEBSITE_STYLES_SECONDARY_BUTTONS_BORDER
  payload: T
}
export interface WebsiteStylesFormFieldBorderAction<
  T extends Partial<BorderType> = Partial<BorderType>,
> {
  type: typeof UPDATE_WEBSITE_STYLES_FORM_FIELD_BORDER
  payload: T
}

export interface WebsiteThemeAction {
  type: typeof SET_WEBSITE_THEME
  payload: WebsiteThemeInterface
}

export interface WebsiteDraftThemeAction {
  type: typeof SET_WEBSITE_DRAFT_THEME
  payload: WebsiteThemeInterface | null
}

export interface WebsiteThemeListAction {
  type: typeof SET_WEBSITE_THEMES_LIST
  payload: WebsiteThemeListInterface[]
}

export interface WebsiteSettingsAction {
  type: typeof SET_WEBSITE_SETTINGS
  payload: WebsitePageDataInterface
}

export interface WebsiteAddLayoutAction {
  type: typeof ADD_WEBSITE_LAYOUT
  payload: {
    locale: PageLocaleEnum
    layoutPageId: number
  }
}

export interface WebsiteSetLayoutByLocaleAction {
  type: typeof SET_WEBSITE_LAYOUT_BY_LOCALE
  payload: {
    locale: PageLocaleEnum
  }
}

export interface WebsiteRemoveLayoutAction {
  type: typeof REMOVE_WEBSITE_LAYOUT
  payload: {
    locale: PageLocaleEnum
  }
}

export interface WebsiteStylesPrimaryButtonsAction {
  type: typeof UPDATE_WEBSITE_STYLES_PRIMARY_BUTTONS
  payload: WebsiteButtonsInterface
}

export interface WebsiteStylesSecondaryButtonsAction {
  type: typeof UPDATE_WEBSITE_STYLES_SECONDARY_BUTTONS
  payload: WebsiteButtonsInterface
}
export interface WebsiteStylesFormFieldAction {
  type: typeof UPDATE_WEBSITE_STYLES_FORM_FIELD
  payload: WebsiteFormFieldsSettings
}

export interface WebsiteThemeLinksAction {
  type: typeof UPDATE_WEBSITE_THEME_LINKS
  payload: WebsiteSettingsInterface['theme']['links']
}

export interface WebsiteUpdateStylesLayoutAction {
  type: typeof UPDATE_WEBSITE_STYLES_LAYOUT_SETTINGS
  payload: Partial<WebsiteLayoutSettings>
}

export type WebsiteSettingsActionType =
  | WebsiteStylesAction
  | WebsiteThemeAction
  | WebsiteDraftThemeAction
  | WebsiteThemeListAction
  | WebsiteSettingsAction
  | WebsiteAddLayoutAction
  | WebsiteRemoveLayoutAction
  | WebsiteNameAction
  | WebsiteCurrentLocaleAction
  | WebsiteLocaleAction
  | WebsiteStylesTypographyParagraphAction
  | WebsiteStylesTypographyHeadingAction
  | WebsiteStylesPrimaryButtonsTypographyAction
  | WebsiteStylesPrimaryButtonsAction
  | WebsiteStylesSecondaryButtonsAction
  | WebsiteStylesPrimaryButtonsBorderAction
  | WebsiteStylesSecondaryButtonsTypographyAction
  | WebsiteThemeLinksAction
  | WebsiteStylesFormFieldTypographyAction
  | WebsiteStylesFormFieldBorderAction
  | WebsiteStylesFormFieldAction
  | WebsiteUpdateStylesLayoutAction
  | WebsiteStylesSecondaryButtonsBorderAction

export const websiteSettingsReducer = (
  state: WebsiteSettingsState = defaultReducerState,
  action: WebsiteSettingsActionType | AnyAction,
): WebsiteSettingsState => {
  switch (action.type) {
    case SET_WEBSITE_STYLES:
      return {
        ...state,
        websiteStyles: action.payload,
      }
    case ADD_WEBSITE_LAYOUT: {
      const { locale, layoutPageId } = action.payload
      const nextLayouts = [
        ...state.websiteLayouts.filter(layout => layout.locale !== locale),
        { id: layoutPageId, locale },
      ]

      return {
        ...state,
        websiteLayouts: nextLayouts,
        websiteLayoutPageId:
          state.currentWebsiteLocale === locale
            ? layoutPageId
            : state.websiteLayoutPageId,
      }
    }
    case REMOVE_WEBSITE_LAYOUT: {
      const { locale } = action.payload
      const nextLayouts = state.websiteLayouts.filter(
        layout => layout.locale !== locale,
      )

      const nextLayoutPageId = getWebsiteLayoutPageIdForLocale({
        layouts: nextLayouts,
        currentLocale:
          state.currentWebsiteLocale ??
          state.websiteLocale ??
          PageLocaleEnum.ENGLISH,
      })

      return {
        ...state,
        websiteLayouts: nextLayouts,
        websiteLayoutPageId: nextLayoutPageId,
      }
    }
    case UPDATE_WEBSITE_STYLES_TYPOGRAPHY_PARAGRAPH:
      return {
        ...state,
        websiteStyles: {
          ...state.websiteStyles,
          typography: {
            ...state.websiteStyles?.typography,
            paragraph: action.payload,
          },
        },
      }
    case UPDATE_WEBSITE_STYLES_TYPOGRAPHY_HEADING:
      return {
        ...state,
        websiteStyles: {
          ...state.websiteStyles,
          typography: {
            ...state.websiteStyles?.typography,
            heading: action.payload,
          },
        },
      }
    case UPDATE_WEBSITE_STYLES_PRIMARY_BUTTONS_TYPOGRAPHY:
      return {
        ...state,
        websiteStyles: {
          ...state.websiteStyles,
          buttonsAndLinks: {
            ...state.websiteStyles?.buttonsAndLinks,
            primary: {
              ...state.websiteStyles?.buttonsAndLinks?.primary,
              typography: action.payload,
            },
          },
        },
      }

    case UPDATE_WEBSITE_STYLES_SECONDARY_BUTTONS_TYPOGRAPHY:
      return {
        ...state,
        websiteStyles: {
          ...state.websiteStyles,
          buttonsAndLinks: {
            ...state.websiteStyles?.buttonsAndLinks,
            secondary: {
              ...state.websiteStyles?.buttonsAndLinks?.primary,
              typography: action.payload,
            },
          },
        },
      }

    case UPDATE_WEBSITE_STYLES_FORM_FIELD_TYPOGRAPHY:
      return {
        ...state,
        websiteStyles: {
          ...state.websiteStyles,
          formFields: {
            ...state.websiteStyles?.formFields,
            typography: action.payload,
          },
        },
      }

    case UPDATE_WEBSITE_STYLES_PRIMARY_BUTTONS:
      return {
        ...state,
        websiteStyles: {
          ...state.websiteStyles,
          buttonsAndLinks: {
            ...state.websiteStyles?.buttonsAndLinks,
            primary: {
              ...state.websiteStyles?.buttonsAndLinks?.primary,
              ...action.payload,
              border: {
                ...(state.websiteStyles?.buttonsAndLinks?.primary?.border ||
                  {}),
                ...action.payload.border,
              },
            },
          },
        },
      }
    case UPDATE_WEBSITE_STYLES_PRIMARY_BUTTONS_BORDER:
      return {
        ...state,
        websiteStyles: {
          ...state.websiteStyles,
          buttonsAndLinks: {
            ...state.websiteStyles?.buttonsAndLinks,
            primary: {
              ...state.websiteStyles?.buttonsAndLinks?.primary,
              border: action.payload,
            },
          },
        },
      }
    case UPDATE_WEBSITE_STYLES_SECONDARY_BUTTONS_BORDER:
      return {
        ...state,
        websiteStyles: {
          ...state.websiteStyles,
          buttonsAndLinks: {
            ...state.websiteStyles?.buttonsAndLinks,
            secondary: {
              ...state.websiteStyles?.buttonsAndLinks?.primary,
              border: action.payload,
            },
          },
        },
      }
    case UPDATE_WEBSITE_STYLES_FORM_FIELD_BORDER:
      return {
        ...state,
        websiteStyles: {
          ...state.websiteStyles,
          formFields: {
            ...state.websiteStyles?.formFields,
            border: action.payload,
          },
        },
      }

    case UPDATE_WEBSITE_STYLES_SECONDARY_BUTTONS:
      return {
        ...state,
        websiteStyles: {
          ...state.websiteStyles,
          buttonsAndLinks: {
            ...state.websiteStyles?.buttonsAndLinks,
            secondary: {
              ...state.websiteStyles?.buttonsAndLinks?.secondary,
              ...action.payload,
            },
          },
        },
      }
    case UPDATE_WEBSITE_STYLES_FORM_FIELD:
      return {
        ...state,
        websiteStyles: {
          ...state.websiteStyles,
          formFields: {
            ...state.websiteStyles?.formFields,
            ...action.payload,
          },
        },
      }
    case UPDATE_WEBSITE_THEME_LINKS:
      if (!state.websiteDraftTheme && !state.websiteTheme) {
        return state
      }

      {
        const baseTheme = state.websiteDraftTheme || state.websiteTheme

        if (!baseTheme) {
          return state
        }

        return {
          ...state,
          websiteDraftTheme: {
            ...baseTheme,
            links: {
              ...baseTheme.links,
              ...action.payload,
            },
          },
        }
      }
    case UPDATE_WEBSITE_STYLES_LAYOUT_SETTINGS:
      return {
        ...state,
        websiteStyles: {
          ...state.websiteStyles,
          layout: {
            ...state.websiteStyles?.layout,
            ...action.payload,
          },
        },
      }
    case SET_WEBSITE_NAME:
      return {
        ...state,
        websiteName: action.payload,
      }
    case SET_WEBSITE_LAYOUT_BY_LOCALE: {
      const nextLayoutPageId = getWebsiteLayoutPageIdForLocale({
        layouts: state.websiteLayouts,
        currentLocale: action.payload.locale,
      })

      return {
        ...state,
        websiteLayoutPageId: nextLayoutPageId,
      }
    }
    case SET_CURRENT_WEBSITE_LOCALE: {
      return {
        ...state,
        currentWebsiteLocale: action.payload,
      }
    }
    case SET_WEBSITE_LOCALE:
      return {
        ...state,
        websiteLocale: action.payload,
      }
    case SET_WEBSITE_DATA_BASELINE:
      return {
        ...state,
        websiteDataBaseline: action.payload,
      }
    case SET_WEBSITE_THEMES_LIST:
      return {
        ...state,
        websiteThemesList: action.payload,
      }
    case SET_WEBSITE_THEME:
      return {
        ...state,
        websiteTheme: action.payload,
      }

    case SET_WEBSITE_DRAFT_THEME:
      return {
        ...state,
        websiteDraftTheme: action.payload,
      }
    case SET_WEBSITE_SETTINGS: {
      const { layouts, website, locale: currentPageLocale } = action.payload

      const resolvedLayoutPageId = getWebsiteLayoutPageIdForLocale({
        layouts,
        currentLocale: currentPageLocale,
      })

      return {
        ...state,
        websiteId: website.id,
        websiteName: website.name,
        websiteDomain: website.userDomain,
        websiteActive: website.active,
        websiteLayouts: layouts,
        websiteLayoutPageId: resolvedLayoutPageId,
        websiteLocale: website.locale,
        currentWebsiteLocale: currentPageLocale,
        websiteStyles: website.style,
        websiteTheme: website.theme,
        websiteDataBaseline: {
          name: website.name,
          defaultLocale: website.locale,
        },
      }
    }
    default:
      return state
  }
}
